// src/components/myAccount/pages/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { User, Save, Calendar } from 'lucide-react';
import SitewideMessage from '../../common/SitewideMessage';

// Common country dial codes
const countryDialCodes = [
  { code: '+1', country: 'US/CA' },
  { code: '+44', country: 'UK' },
  { code: '+61', country: 'AU' },
  { code: '+64', country: 'NZ' },
  { code: '+27', country: 'ZA' },
  { code: '+33', country: 'FR' },
  { code: '+49', country: 'DE' },
  { code: '+39', country: 'IT' },
  { code: '+34', country: 'ES' },
  { code: '+31', country: 'NL' },
  { code: '+32', country: 'BE' },
  { code: '+41', country: 'CH' },
  { code: '+43', country: 'AT' },
  { code: '+45', country: 'DK' },
  { code: '+46', country: 'SE' },
  { code: '+47', country: 'NO' },
  { code: '+48', country: 'PL' },
  { code: '+351', country: 'PT' },
  { code: '+353', country: 'IE' },
  { code: '+358', country: 'FI' },
  { code: '+7', country: 'RU/KZ' },
  { code: '+81', country: 'JP' },
  { code: '+82', country: 'KR' },
  { code: '+86', country: 'CN' },
  { code: '+91', country: 'IN' },
  { code: '+65', country: 'SG' },
  { code: '+60', country: 'MY' },
  { code: '+66', country: 'TH' },
  { code: '+62', country: 'ID' },
  { code: '+63', country: 'PH' },
  { code: '+84', country: 'VN' },
  { code: '+971', country: 'AE' },
  { code: '+974', country: 'QA' },
  { code: '+965', country: 'KW' },
  { code: '+973', country: 'BH' },
  { code: '+968', country: 'OM' },
  { code: '+966', country: 'SA' },
  { code: '+972', country: 'IL' },
  { code: '+20', country: 'EG' },
  { code: '+234', country: 'NG' },
  { code: '+254', country: 'KE' },
  { code: '+55', country: 'BR' },
  { code: '+52', country: 'MX' },
  { code: '+54', country: 'AR' },
  { code: '+56', country: 'CL' },
  { code: '+57', country: 'CO' },
  { code: '+51', country: 'PE' }
];

// Extract country code and phone number from existing phone field
const parsePhoneNumber = (phone) => {
  if (!phone) return { dialCode: '+1', number: '' };
  const match = phone.match(/^(\+\d{1,4})\s?(.+)$/);
  if (match) {
    return { dialCode: match[1], number: match[2] };
  }
  // If no country code found, check if it starts with a known code
  const foundCode = countryDialCodes.find(c => phone.startsWith(c.code));
  if (foundCode) {
    return { dialCode: foundCode.code, number: phone.substring(foundCode.code.length).trim() };
  }
  return { dialCode: '+1', number: phone };
};

const ProfilePage = ({ user, userProfile, onRefreshData }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    username: '',
    date_of_birth: '',
    phone_dial_code: '+1',
    phone: '',
    address: {
      street: '',
      line2: '',
      suburb: '',
      postcode: '',
      state: '',
      country: ''
    }
  });

  // Update form data when userProfile changes
  useEffect(() => {
    if (userProfile) {
      const initialPhone = parsePhoneNumber(userProfile?.phone || '');
      setFormData({
        first_name: userProfile?.first_name || '',
        middle_name: userProfile?.middle_name || '',
        last_name: userProfile?.last_name || '',
        username: userProfile?.username || '',
        date_of_birth: userProfile?.date_of_birth || '',
        phone_dial_code: initialPhone.dialCode,
        phone: initialPhone.number,
        address: {
          street: userProfile?.address_json?.street || '',
          line2: userProfile?.address_json?.line2 || '',
          suburb: userProfile?.address_json?.suburb || '',
          postcode: userProfile?.address_json?.postcode || '',
          state: userProfile?.address_json?.state || '',
          country: userProfile?.address_json?.country || ''
        }
      });
    }
  }, [userProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      // Combine dial code and phone number
      const fullPhoneNumber = formData.phone_dial_code && formData.phone 
        ? `${formData.phone_dial_code} ${formData.phone}`.trim()
        : formData.phone || '';

      // Prepare update data (exclude phone_dial_code, combine into phone)
      const updateData = {
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        username: formData.username,
        date_of_birth: formData.date_of_birth,
        phone: fullPhoneNumber,
        address_json: formData.address
      };

      // Import updateUserProfile function
      const { updateUserProfile } = await import('../../../services/authService');
      await updateUserProfile(user.id, updateData);
      
      setMessage('Profile updated successfully!');
      setIsEditing(false);
      
      if (onRefreshData) {
        onRefreshData();
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data
    const resetPhone = parsePhoneNumber(userProfile?.phone || '');
    setFormData({
      first_name: userProfile?.first_name || '',
      middle_name: userProfile?.middle_name || '',
      last_name: userProfile?.last_name || '',
      username: userProfile?.username || '',
      date_of_birth: userProfile?.date_of_birth || userProfile?.dob || '',
      phone_dial_code: resetPhone.dialCode,
      phone: resetPhone.number,
      address: {
        street: userProfile?.address_json?.street || '',
        line2: userProfile?.address_json?.line2 || '',
        suburb: userProfile?.address_json?.suburb || '',
        postcode: userProfile?.address_json?.postcode || '',
        state: userProfile?.address_json?.state || '',
        country: userProfile?.address_json?.country || ''
      }
    });
    setIsEditing(false);
    setMessage('');
    setError('');
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Zone 2 - Below header (only on dashboard, profile, login-details) */}
      <div className="mb-6">
        <SitewideMessage zone="zone2" userRoles={[]} />
      </div>
      
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
            {userProfile?.is_super_admin && (
              <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-semibold rounded-full">
                Super Admin
              </span>
            )}
          </div>
          <p className="text-gray-600 mt-2">Manage your personal information and preferences.</p>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <User className="h-4 w-4 mr-2" />
            Edit Profile
          </button>
        )}
      </div>

      {/* Messages */}
      {message && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {message}
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200">
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Name Fields */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Name</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Middle Name
                    </label>
                    <input
                      type="text"
                      name="middle_name"
                      value={formData.middle_name}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Contact Fields */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Contact</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <div className="flex">
                      <select
                        name="phone_dial_code"
                        value={formData.phone_dial_code}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500 bg-white"
                        style={{ width: '120px' }}
                      >
                        {countryDialCodes.map(({ code, country }) => (
                          <option key={code} value={code}>
                            {code} {country}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Phone number"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500 border-l-0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Email cannot be changed here. Use Login Details to update.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Date of Birth - Full Width Below */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>
              <div className="relative max-w-xs">
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
                <Calendar className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  name="address.street"
                  value={formData.address.street}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address Line 2 (Optional)
                </label>
                <input
                  type="text"
                  name="address.line2"
                  value={formData.address.line2}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City/Suburb
                </label>
                <input
                  type="text"
                  name="address.suburb"
                  value={formData.address.suburb}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Postcode
                </label>
                <input
                  type="text"
                  name="address.postcode"
                  value={formData.address.postcode}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State
                </label>
                <input
                  type="text"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;